import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { Loader2, UserCog } from 'lucide-react'
import { useUpdateProfessional } from '../hooks/use-professionals'
import { useToast } from '@/hooks/use-toast'
import { PROFESSIONAL_SPECIALTIES } from '../constants/professional-specialties'

const editProfessionalSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email invalido'),
  specialty: z.string().optional(),
  role: z.enum(['admin', 'professional']),
})

type EditProfessionalFormValues = z.infer<typeof editProfessionalSchema>

interface Professional {
  id: string
  name: string
  email: string
  specialty?: string | null
  role: 'admin' | 'professional'
}

interface EditProfessionalModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  professional: Professional | null
}

export function EditProfessionalModal({
  open,
  onOpenChange,
  professional,
}: EditProfessionalModalProps) {
  const { toast } = useToast()
  const updateProfessional = useUpdateProfessional()

  const form = useForm<EditProfessionalFormValues>({
    resolver: zodResolver(editProfessionalSchema),
    defaultValues: {
      name: '',
      email: '',
      specialty: '',
      role: 'professional',
    },
  })

  useEffect(() => {
    if (professional) {
      form.reset({
        name: professional.name,
        email: professional.email,
        specialty: professional.specialty ?? '',
        role: professional.role,
      })
    }
  }, [professional, form])

  const onSubmit = async (data: EditProfessionalFormValues) => {
    if (!professional) return

    try {
      await updateProfessional.mutateAsync({
        id: professional.id,
        ...data,
      })
      toast({
        title: 'Profissional atualizado',
        description: 'As alteracoes foram salvas com sucesso.',
      })
      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Erro',
        description: (error as Error).message ?? 'Nao foi possivel atualizar o profissional.',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Editar Profissional
          </DialogTitle>
          <DialogDescription>
            Atualize as informacoes do profissional. Alteracoes de email exigirao reautenticacao.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome completo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="email@exemplo.com" {...field} />
                  </FormControl>
                  <FormDescription>
                    Alteracoes de email exigem verificacao
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="specialty"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Especialidade</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value ?? ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma especialidade" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PROFESSIONAL_SPECIALTIES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Funcao</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma funcao" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="professional">Profissional</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Administradores podem gerenciar profissionais e pacientes
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={updateProfessional.isPending}>
                {updateProfessional.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Salvar Alteracoes
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
